/**
 * API Key Rate Limits Dialog Component
 *
 * Dialog for updating rate limits on an API key.
 */

import { useState, useEffect } from 'react';
import { Settings2, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateApiKeyRateLimits } from '@/hooks/use-api-keys';
import { useToast } from '@/hooks/use-toast';
import type { ApiKey } from '@/types/api-key';

interface ApiKeyRateLimitsDialogProps {
  apiKey: ApiKey | null;
  onClose: () => void;
}

/**
 * Form Schema
 * Validation schema for rate limits form
 */
const rateLimitsFormSchema = z.object({
  maxRequests: z.number().int().min(1, 'Must be at least 1'),
  windowMs: z.number().int().min(1000, 'Must be at least 1000ms (1 second)'),
});

type RateLimitsFormValues = z.infer<typeof rateLimitsFormSchema>;

/**
 * API Key Rate Limits Dialog
 * Renders form for updating rate limits
 */
export function ApiKeyRateLimitsDialog({ apiKey, onClose }: ApiKeyRateLimitsDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateRateLimitsMutation = useUpdateApiKeyRateLimits();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<RateLimitsFormValues>({
    resolver: zodResolver(rateLimitsFormSchema),
    defaultValues: {
      maxRequests: apiKey?.maxRequests || 1000,
      windowMs: apiKey?.windowMs || 300000,
    },
  });

  // Update form when apiKey changes
  useEffect(() => {
    if (apiKey) {
      setValue('maxRequests', apiKey.maxRequests);
      setValue('windowMs', apiKey.windowMs);
    }
  }, [apiKey, setValue]);

  const onSubmit = async (data: RateLimitsFormValues) => {
    if (!apiKey) return;

    setIsSubmitting(true);
    try {
      await updateRateLimitsMutation.mutateAsync({
        keyId: apiKey.id,
        data: {
          maxRequests: data.maxRequests,
          windowMs: data.windowMs,
        },
      });

      toast({
        title: 'Rate Limits Updated',
        description: `${apiKey.keyName} rate limits have been updated`,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: error instanceof Error ? error.message : 'Failed to update rate limits',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Convert milliseconds to minutes for display
  const windowMinutes = apiKey ? Math.round(apiKey.windowMs / 60000) : 5;

  return (
    <Dialog open={apiKey !== null} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Update Rate Limits
          </DialogTitle>
          <DialogDescription>
            Configure the rate limit settings for {apiKey?.keyName}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="bg-muted p-4 rounded-md space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Key Name:</span>
              <span className="font-medium">{apiKey?.keyName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Current Limit:</span>
              <span className="font-mono text-xs">
                {apiKey?.maxRequests.toLocaleString()} requests / {windowMinutes} min
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxRequests">Max Requests</Label>
            <Input
              id="maxRequests"
              type="number"
              {...register('maxRequests', { valueAsNumber: true })}
              placeholder="e.g., 1000"
            />
            {errors.maxRequests && (
              <p className="text-sm text-destructive">{errors.maxRequests.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Maximum number of requests allowed within the time window
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="windowMs">Time Window (milliseconds)</Label>
            <Input
              id="windowMs"
              type="number"
              {...register('windowMs', { valueAsNumber: true })}
              placeholder="e.g., 300000"
            />
            {errors.windowMs && (
              <p className="text-sm text-destructive">{errors.windowMs.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Time window in milliseconds (e.g., 300000 = 5 minutes)
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Rate Limits'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
